import { errorToast, infoToast, warningToast } from '@/helpers.tsx';
import { _noop, _notNil, getJwtExpiry } from '@/littledash.ts';
import {
  type PlatformLoginRequest,
  type PlatformLoginResponse,
  type TeamSummary,
  type UserSummary,
} from '@/model/Auth.model.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService.ts';
import { consumePostLogout, type LogoutMessage, logout as authLogout } from '@/support/auth.ts';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController.ts';
import useMountedState from '@/support/Hooks/fetch/useMountedState.ts';
import Http from '@/support/http.ts';
import { web as webRoute } from '@/support/route.ts';
import { RouteService } from '@/support/RouteService.ts';
import { ApplicationEvents } from '@/utils/ApplicationEvents.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { TeamChangeDetector } from '@/utils/TeamChangeDetector.ts';
import { type AxiosRequestConfig } from 'axios';
import { type Duration, intervalToDuration, isBefore } from 'date-fns';
import { createElement, type Reducer, useCallback, useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { parse as parseDuration } from 'tinyduration';

export interface LoginState {
  sso: SsoConfig;
  redeem_loading: boolean;
  platformToken?: {
    token: string;
    expiry: Date;
  };
  platformTokenExpired: boolean;
  user?: UserSummary;
  teams: Array<TeamSummary>;
  token_valid_duration?: Duration;
}

export type SsoConfig = { enabled: boolean; type?: string };
export type LoginSubmitResult =
  | { type: 'success' }
  | { type: 'unauthorised' }
  | { type: 'cooldown'; cooldownDuration: Duration }
  | { type: 'unknown-error' };
export type LoginSubmit = (login: PlatformLoginRequest) => Promise<LoginSubmitResult>;
export type LoginActions =
  | { type: 'platform_login'; data: PlatformLoginResponse }
  | { type: 'sso_config'; data: SsoConfig }
  | { type: 'redeem_loading'; data: boolean }
  | { type: 'token_valid_duration'; data?: Duration };
export const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/**
 * A failed login returns 401, which the shared axios interceptor would otherwise
 * treat as an expired session — reloading the page and showing a "Session expired"
 * toast. Opt out so the login form can surface its own inline "credentials do not
 * match" error instead.
 */
const NO_REDIRECT_ON_401 = { redirect: { 401: false } } as AxiosRequestConfig;

const DEFAULT_LOGIN_COOLDOWN = 'PT1M';

/**
 * sessionStorage key used to survive the SSO round-trip on export-download
 * deep links. Written by PrivateRoute (App.tsx) when an unauthenticated user
 * hits a route that opted in via `preserveDeepLinkOnLogin`, then consumed by
 * the post-login navigation below after the short-lived-token redemption
 * brings the user back.
 */
export const PENDING_EXPORT_REDIRECT_KEY = 'pendingExportRedirect';

/**
 * Read and clear the deep-link target stashed in sessionStorage by PrivateRoute
 * when an unauthenticated user clicks an export-download link from an email.
 * Returns the path only if it's a same-origin relative URL — anything else is
 * treated as untrusted (potential open-redirect) and discarded.
 */
const consumePendingExportRedirect = (): string | undefined => {
  const stored = window.sessionStorage.getItem(PENDING_EXPORT_REDIRECT_KEY);
  if (!_notNil(stored)) {
    return undefined;
  }
  window.sessionStorage.removeItem(PENDING_EXPORT_REDIRECT_KEY);
  if (!stored.startsWith('/') || stored.startsWith('//')) {
    return undefined;
  }
  return stored;
};

/**
 * Re-raise a toast carried across a post-logout reload. Rebuilds the title/body
 * markup and routes to the toast helper matching the message level.
 */
const showLogoutToast = ({ level, title, body }: LogoutMessage): void => {
  const content = createElement(
    'div',
    null,
    createElement('h3', { className: 'white' }, title),
    _notNil(body) ? createElement('p', null, body) : null
  );
  (level === 'warning' ? warningToast : infoToast)(content);
};

const loginReducer: Reducer<LoginState, LoginActions> = (prevState, action) => {
  switch (action.type) {
    case 'platform_login': {
      const tokenExpiry = getJwtExpiry(action.data.token);
      const platformTokenExpired = isBefore(new Date(), tokenExpiry as Date);
      return {
        ...prevState,
        user: action.data.user,
        teams: action.data.teams,
        platformToken: {
          token: action.data.token,
          expiry: tokenExpiry as Date,
        },
        platformTokenExpired,
      };
    }
    case 'sso_config': {
      return { ...prevState, sso: action.data };
    }
    case 'redeem_loading': {
      return { ...prevState, redeem_loading: action.data };
    }
    case 'token_valid_duration': {
      return { ...prevState, token_valid_duration: action.data };
    }
    default: {
      return prevState;
    }
  }
};

export const useLoginApi = () => {
  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, dispatch] = useReducer(loginReducer, null, () => ({
    teams: [],
    redeem_loading: false,
    platformTokenExpired: false,
    sso: { enabled: false },
  }));
  const postLogoutReturnTo = useRef<string | undefined>(undefined);

  useEffect(() => {
    const post = consumePostLogout();
    if (_notNil(post?.message)) {
      showLogoutToast(post.message);
    }
    postLogoutReturnTo.current = post?.returnTo;
  }, []);

  useEffect(() => {
    if (_notNil(state?.platformToken?.expiry)) {
      dispatch({
        type: 'token_valid_duration',
        data: intervalToDuration({
          start: new Date(),
          end: state?.platformToken?.expiry as Date,
        }),
      });
      const intervalId = setInterval(() => {
        const duration = intervalToDuration({
          start: new Date(),
          end: state?.platformToken?.expiry as Date,
        });
        if (Object.values(duration).every((v) => v <= 0)) {
          authLogout({ type: 'inactivity' });
        } else {
          dispatch({
            type: 'token_valid_duration',
            data: duration,
          });
        }
      }, 1000);
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [dispatch, state?.platformToken?.expiry]);

  const { newAbortController } = useAbortController();
  const isMounted = useMountedState();
  const loadConfig = useCallback(async (): Promise<void> => {
    const ssoConfig = await Http.get(RouteService.legacyApi({ apiRoute: 'config' }).url.href, {
      signal: newAbortController().signal,
      fetchOptions: { addAuth: false },
    })
      .then((response) => {
        const ssoType = response?.data?.ssoType ?? false;
        return typeof ssoType === 'string' ? { enabled: true, type: ssoType } : { enabled: false };
      })
      .catch((cause) => {
        if (notAborted(cause)) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to load SSO config', { slug: 'sso-config', cause })
          );
        }
        return { enabled: false };
      });
    if (isMounted()) {
      dispatch({ type: 'sso_config', data: ssoConfig });
    }
  }, [newAbortController, isMounted]);

  const platformLogin = useCallback<LoginSubmit>(
    async (login) => {
      return ApiService.call({
        endpoint: 'POST /api/v1/auth/login',
        body: login,
        options: {
          onError: { throw: false, capture: false, toast: false },
          requestConfigOverrides: NO_REDIRECT_ON_401,
        },
      })
        .then((response): LoginSubmitResult => {
          if (response.type === 'error') {
            switch (response.error.response?.status) {
              case 401: {
                ExceptionHandler.captureException(
                  new InVivoError('Platform credentials incorrect', {
                    slug: 'platform-credentials-incorrect',
                    level: 'log',
                    context: { code: '401' },
                  })
                );
                return { type: 'unauthorised' };
              }
              case 429: {
                // `cooldown` reaches us through the error body's additional properties, so it
                // arrives untyped and a malformed value would break `parseDuration`.
                const reportedCooldown = response.error.response?.data?.cooldown;
                const cooldown = typeof reportedCooldown === 'string' ? reportedCooldown : DEFAULT_LOGIN_COOLDOWN;
                ExceptionHandler.captureException(
                  new InVivoError('Too many platform login attempts', {
                    slug: 'too-many-platform-login-attempts',
                    level: 'log',
                    context: { code: '429', cooldown },
                  })
                );
                return { type: 'cooldown', cooldownDuration: parseDuration(cooldown) };
              }
              default:
                ExceptionHandler.captureException(
                  new InVivoError('Platform login api failure', {
                    slug: 'platform-login-api-failure',
                    context: { code: `${response.error.response?.status}` },
                  })
                );
                return { type: 'unknown-error' };
            }
          }
          if (isMounted()) {
            dispatch({ type: 'platform_login', data: response.body });
            reduxDispatch({
              type: 'USER_SET_CURRENT_USER',
              currentUser: response.body?.user,
            });
          }
          return { type: 'success' } as LoginSubmitResult;
        })
        .catch((cause): LoginSubmitResult => {
          ExceptionHandler.captureException(
            new InVivoError('Platform login api failure', { slug: 'platform-login-api-failure', cause })
          );
          return { type: 'unknown-error' };
        });
    },
    [isMounted, dispatch, reduxDispatch]
  );

  const teamLogin = useCallback(
    async (team: TeamSummary, singleTeam: boolean) => {
      return ApiService.call({
        endpoint: 'POST /api/v1/auth/team/{teamApiId}/login',
        headers: { Authorization: `Bearer ${state.platformToken?.token ?? ''}` },
        path: { teamApiId: team.api_id },
        options: {
          requestConfigOverrides: { withCredentials: true },
          onError: { throw: true, capture: false, toast: false, slug: 'team-login' },
        },
      })
        .then((response) => {
          if (isMounted()) {
            if (response.type === 'success') {
              const fromPathName =
                postLogoutReturnTo.current ??
                (location.state?.from?.pathname ? encodeURI(location.state?.from?.pathname) : undefined);
              const exportRedirect = consumePendingExportRedirect();
              window.localStorage.setItem('team_id', team.api_id);
              reduxDispatch({
                type: 'LOGIN',
                token: response.body.token,
              });
              TeamChangeDetector.broadcastTeamChange();
              ApplicationEvents.authenticated();
              if (singleTeam && fromPathName && fromPathName !== '/') {
                navigate(fromPathName);
              } else if (_notNil(exportRedirect) && exportRedirect !== '/') {
                // Honor the deep-link regardless of singleTeam: the user has
                // explicitly picked a team via the picker, and the export
                // download routes will 404 cleanly if the export belongs to a
                // different team they happen to also be a member of.
                navigate(exportRedirect);
              } else {
                navigate(webRoute('dashboard'));
              }
            } else {
              errorToast('Team login failed ... please try again later');
            }
          }
        })
        .catch((cause) => {
          const err = new InVivoError('Team auth failure', { cause, slug: 'team-login-failure' });
          if (cause instanceof InVivoError && cause.context?.ApiStatusCode === '401') {
            authLogout({ type: 'user' });
          }
          if (isMounted()) {
            errorToast('Team login failed ... please try again');
          }
          ExceptionHandler.captureException(err);
          return Promise.reject(err);
        });
    },
    [history, reduxDispatch, isMounted, state.platformToken]
  );

  const redeemShortLivedToken = useCallback(
    async (token: string) => {
      dispatch({ type: 'redeem_loading', data: true });
      return ApiService.call({
        endpoint: 'POST /api/v1/short-lived/auth/redeem',
        headers: { Authorization: `Bearer ${token}` },
        options: {
          requestConfigOverrides: { withCredentials: true, ...NO_REDIRECT_ON_401 },
          onError: { throw: true, capture: false, toast: false, slug: 'slt-redeem' },
        },
      })
        .then((response) => {
          if (isMounted()) {
            if (response.type === 'success') {
              const exportRedirect = consumePendingExportRedirect();
              window.localStorage.setItem('team_id', response.body.team_token);
              reduxDispatch({
                type: 'LOGIN',
                token: response.body.bearer_token,
              });
              TeamChangeDetector.broadcastTeamChange();
              ApplicationEvents.authenticated();
              if (_notNil(exportRedirect) && exportRedirect !== '/') {
                navigate(exportRedirect);
              } else {
                navigate(webRoute('dashboard'));
              }
            } else {
              dispatch({ type: 'redeem_loading', data: false });
              errorToast('Authentication failed ... please try again');
            }
          }
        })
        .catch((cause) => {
          if (isMounted()) {
            dispatch({ type: 'redeem_loading', data: false });
            errorToast('Authentication failed ... please try again');
          }
          ExceptionHandler.captureException(
            new InVivoError('Short lived token redeem failure', {
              slug: 'slt-failure',
              cause,
            })
          );
        });
    },
    [reduxDispatch, history, isMounted]
  );

  const logout = useCallback(() => {
    authLogout({ type: 'user' });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location?.search ?? '');
    if (params.has('identifier')) {
      // Discard any leftover token from a prior session before redeeming. The SLT
      // handshake carries its own bearer header, so this cannot affect the request —
      // but leaving a stale token in storage lets ExpiredJwtDetector race the
      // redemption and force a session-expired logout that reloads away the flow.
      authLogout({ type: 'stale-token' });
      redeemShortLivedToken(params.get('identifier') as string).catch(_noop);
    }
  }, [location.search, redeemShortLivedToken]);

  return {
    state,
    dispatch,
    platformLogin,
    teamLogin,
    loadConfig,
    logout,
  };
};
